// frontend/src/pages/TrustsIndex.tsx
import React from "react";
import { Link } from "react-router-dom";
import { apiTrustsIndex, type TrustEntity } from "../lib/api";

function statusClasses(status: string): string {
  const s = status.toLowerCase();

  if (s === "rejected") {
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200";
  }

  if (s === "active" || s === "approved") {
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200";
  }

  if (s === "pending" || s === "submitted" || s === "processing") {
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-orange-100 text-orange-700 border-orange-200";
  }

  if (s === "terminated") {
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-200 text-gray-700 border-gray-300";
  }

  return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200";
}

function jurisdictionLabelFrom(t: TrustEntity): string {
  const rawCode = (// @ts-expect-error allow backend field
    t.jurisdiction_code || t.jurisdiction || ""
  )
    .toString()
    .toLowerCase()
    .trim();

  const rawText = (t.jurisdiction || "").toString().trim();

  switch (rawCode) {
    case "bahamas":
      return "The Bahamas – Purpose Trust";
    case "wyoming":
      return "Wyoming (USA) – Asset Protection Trust";
    case "uk":
      return "United Kingdom – Discretionary Trust";
    default:
      // If we already have a nice label stored, use that
      if (rawText && rawText.toLowerCase() !== rawCode) return rawText;
      if (!rawCode) return "—";
      return rawCode.toUpperCase();
  }
}

export default function TrustsIndex() {
  const [rows, setRows] = React.useState<TrustEntity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiTrustsIndex();
        if (!alive) return;
        setRows(res.trusts || []);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load trusts.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const hasAny = rows.length > 0;
  const latest: TrustEntity | null = hasAny ? rows[0] : null;
  const latestStatus = (latest?.status || "").toLowerCase();

  const canApply = !hasAny;
  const canReapply = !!latest && latestStatus === "rejected";

  return (
    <div className="section-box p-4 sm:p-6 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Your Trusts</h1>

        <div className="flex gap-2">
          {canApply && (
            <Link className="btn btn-success" to="/trust/apply">
              Apply
            </Link>
          )}

          {canReapply && latest && (
            <Link className="btn btn-success" to={`/trusts/${latest.id}/reapply`}>
              Reapply
            </Link>
          )}
        </div>
      </div>

      {/* Loading / error / empty */}
      {loading && <div className="skeleton h-24" />}

      {!loading && error && (
        <div className="alert-danger text-sm mb-3">{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="alert-info">
          No trusts yet. Click <span className="font-semibold">Apply</span> to
          start.
        </div>
      )}

      {/* List */}
      {!loading && !error && rows.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="hidden md:grid grid-cols-5 text-xs uppercase tracking-wide text-gray-500 pb-2 border-b">
            <div>ID</div>
            <div>Name</div>
            <div>Status</div>
            <div>Jurisdiction</div>
            <div>Actions</div>
          </div>

          <div className="divide-y">
            {rows.map((t) => {
              const status = String(t.status || "").toLowerCase();
              const isRejected = status === "rejected";
              const jurisdictionLabel = jurisdictionLabelFrom(t);
              const canOpen = status === "active" || status === "approved";

              return (
                <div
                  key={t.id}
                  className="grid md:grid-cols-5 gap-2 py-3 items-start md:items-center"
                >
                  {/* ID */}
                  <div className="text-sm">#{t.id}</div>

                  {/* Name + rejection reason */}
                  <div className="text-sm">
                    <div>{t.trust_name || "Untitled trust"}</div>

                    {isRejected && t.rejection_reason && (
                      <div className="mt-2 rounded-md bg-red-50 border border-red-100 px-3 py-2">
                        <p className="text-[11px] font-semibold text-red-700">
                          Rejection reason from admin
                        </p>
                        <p className="text-[11px] text-red-700 whitespace-pre-line">
                          {t.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Status pill */}
                  <div className="text-sm">
                    <span className={statusClasses(status)}>
                      {status || "—"}
                    </span>
                  </div>

                  {/* Jurisdiction */}
                  <div className="text-sm">
                    <span className="block text-xs text-gray-500 mb-0.5">
                      Jurisdiction
                    </span>
                    <span>{jurisdictionLabel}</span>
                  </div>

                  {/* Actions */}
                  <div className="mt-2 md:mt-0">
                    {canOpen && (
                      <Link className="btn btn-white" to={`/trusts/${t.id}`}>
                        Open
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
